/**
 * Momento "wow" al completar el onboarding esencial (hoy solo `fiscal`, la
 * identidad fiscal). Sigue el lenguaje visual de `src/pages/FirmWowScreen.jsx`
 * (el único otro momento "wow" del producto): icono con spring, headline,
 * detalle — aquí como modal en vez de página, porque debe poder aparecer
 * sobre cualquier pantalla, no solo Home.
 *
 * Ofrece dos caminos opcionales (tour guiado / hablar con ARIA) sin forzar
 * ninguno — cerrar sin elegir no persiste nada, igual que el confetti que
 * sustituye (ver `Layout.jsx`, `showCelebration`).
 */

import { PartyPopper, Compass, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ConfettiBurst from '@/components/asesor/ConfettiBurst';

export default function OnboardingCelebration({ open, companyName, onClose, onStartTour, onTalkToAria }) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        size="full"
        className="flex flex-col items-center justify-center gap-6 border-none bg-gradient-to-b from-primary/10 via-card to-card text-center"
      >
        <ConfettiBurst trigger={open} />

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 ring-8 ring-primary/5"
        >
          <PartyPopper className="h-10 w-10 text-primary" />
        </motion.div>

        <div className="max-w-md space-y-2">
          <DialogTitle className="text-2xl font-semibold tracking-tight">
            {t('onboarding.celebration.title', {
              company: companyName || t('onboarding.celebration.company_fallback', 'Tu empresa'),
              defaultValue: '¡{{company}} ya está lista para funcionar!',
            })}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t('onboarding.celebration.subtitle', 'Has completado la configuración esencial. Ahora puedes facturar, contabilizar y dejar que ARIA te ayude con el resto.')}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button size="lg" variant="outline" onClick={onStartTour} className="gap-2">
            <Compass className="h-4 w-4" />
            {t('onboarding.celebration.cta_tour', 'Enséñamelo')}
          </Button>
          <Button size="lg" onClick={onTalkToAria} className="gap-2">
            <Sparkles className="h-4 w-4" />
            {t('onboarding.celebration.cta_aria', 'Hablar con ARIA')}
          </Button>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          {t('onboarding.celebration.skip', 'Ahora no')}
        </button>
      </DialogContent>
    </Dialog>
  );
}
